import sortBy from 'lodash/sortBy';
import { createStore, createHook, createContainer } from 'react-sweet-state';
import { actions } from './actions';
import { handlers } from './handlers';
import type { State, ContainerProps, LazyGoal, Goal } from './types';

export const GoalsContainer = createContainer<ContainerProps>();

const Store = createStore<State, typeof actions, ContainerProps>({
	containedBy: GoalsContainer,
	initialState: {
		lazyGoalsByARI: {},
		isError: false,
		searchesInProgress: 0,
	},
	actions,
	handlers,
});

const sortLazyGoals = (goals: LazyGoal[]) => sortBy(goals, (goal) => goal.goal?.name);
const getGoalByName = (state: State, searchString: string) =>
	sortLazyGoals(
		Object.values(state.lazyGoalsByARI).filter((lazyGoal) =>
			lazyGoal.goal?.name.toLowerCase().includes(searchString),
		),
	);

const getGoalByIssueGoals = (state: State, goalIds: string[]) =>
	sortLazyGoals(goalIds.map((goalId) => state.lazyGoalsByARI[goalId]));

const getAllLazyGoals = (state: State) => sortLazyGoals(Object.values(state.lazyGoalsByARI));

export const useLazyGoalByName = createHook(Store, {
	selector: getGoalByName,
});

export const useLazyGoals = createHook(Store);

export const useLazyGoalsByARI = createHook(Store, {
	selector: getGoalByIssueGoals,
});

export const useAllLazyGoals = createHook(Store, {
	selector: getAllLazyGoals,
});
const getDefinedGoals = (state: State) => {
	const definedGoalsByARI = Object.entries(state.lazyGoalsByARI).reduce<Record<string, Goal>>(
		(acc, [goalARI, lazyGoal]) => {
			if (lazyGoal.goal !== undefined && !lazyGoal.isLoading) {
				const { goal } = lazyGoal;
				acc[goalARI] = goal;
			}
			return acc;
		},
		{},
	);

	return definedGoalsByARI;
};

export const useDefinedGoals = createHook(Store, {
	selector: getDefinedGoals,
});

const getLazyGoalSearchesInProgress = (state: State) => state.searchesInProgress;

export const useLazyGoalSearchesInProgress = createHook(Store, {
	selector: getLazyGoalSearchesInProgress,
});

export type { LazyGoal, Goal };
